import measurementunitService from '../../../services/measurementunitService';
import filterService from '../../../services/filterService';

import {
    setMeasurementUnitLoading,
    setMeasurementUnitError,
    setMeasurementUnitList,
    setMeasurementUnitCurrent,
    setMeasurementUnitPagination,
    setMeasurementUnitSort,
    setMeasurementUnitFilters,
    setMeasurementUnitDefaultFilter,
    setMeasurementUnitSettings,
    clearMeasurementUnitCurrent,
    clearMeasurementUnitError,
    setMeasurementUnitFilterLoading,
    changeMeasurementUnitFilterCondition,
    addMeasurementUnitFilterCondition,
    updateMeasurementUnitFilterConditionRule,
    updateMeasurementUnitFilterConditionValue,
    removeMeasurementUnitFilterCondition,
    resetMeasurementUnitFiltersAndPagination
} from './measurementunitSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().measurementunit.isSettingsInitialized) {
            dispatch(setMeasurementUnitLoading(true));
            const response = await measurementunitService.getSettings();

            // Set the settings
            dispatch(setMeasurementUnitSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setMeasurementUnitDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setMeasurementUnitError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setMeasurementUnitLoading(false));
    }
};

export const fetchMeasurementunits = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().measurementunit;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setMeasurementUnitLoading(true));
        const response = await measurementunitService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setMeasurementUnitList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchMeasurementunits:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setMeasurementUnitError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setMeasurementUnitLoading(false));
    }
};

export const fetchMeasurementUnit = (id) => async (dispatch) => {
    try {
        dispatch(setMeasurementUnitLoading(true));
        const response = await measurementunitService.getOne(id);
        dispatch(setMeasurementUnitCurrent(response.data));
    } catch (error) {
        dispatch(setMeasurementUnitError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setMeasurementUnitLoading(false));
    }
};

export const createMeasurementUnit = (data) => async (dispatch) => {
    try {
        const response = await measurementunitService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateMeasurementUnit = (id, data) => async (dispatch) => {
    try {
        const response = await measurementunitService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteMeasurementUnit = (id) => async (dispatch) => {
    try {
        dispatch(setMeasurementUnitLoading(true));
        const response = await measurementunitService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setMeasurementUnitError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setMeasurementUnitLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setMeasurementUnitPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setMeasurementUnitSort(sort));
};

export const searchMeasurementunits = (query) => (dispatch, getState) => {
    const currentFilters = getState().measurementunit.filters;
    dispatch(setMeasurementUnitFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setMeasurementUnitDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeMeasurementUnitFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addMeasurementUnitFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateMeasurementUnitFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateMeasurementUnitFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeMeasurementUnitFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setMeasurementUnitFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().measurementunit.settings;
            dispatch(setMeasurementUnitSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setMeasurementUnitFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setMeasurementUnitFilterLoading(false));
        dispatch(setMeasurementUnitError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetMeasurementUnitFiltersAndPagination());
};